import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { getAuthenticatedAxios, getApiUrl } from '../utils/api';

const API_URL = getApiUrl();
const BASE = `${API_URL}/api/market-maker/dump-buyback`;

const fmtTok = (n) => (n >= 1e6 ? `${(n / 1e6).toFixed(2)}M` : n >= 1e3 ? `${(n / 1e3).toFixed(1)}K` : Math.round(n || 0));
const fmtPrice = (p) => (!p ? '$0' : `$${p.toFixed(9).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '')}`);

const DumpBuyback = () => {
  const [groups, setGroups] = useState([]);
  const [form, setForm] = useState({
    sell_group: '', total_tokens: '', wallet_count: '', duration_minutes: 10,
    extra_wallet: '', buyback_mode: 'aggressive', buyback_slippage: 15,
    gentle_chunk_usd: 5, gentle_slippage: 1, buyback_pause_seconds: 45,
    chunk_min: '', chunk_max: '',
  });
  const [plan, setPlan] = useState(null);
  const [planning, setPlanning] = useState(false);
  const [taskId, setTaskId] = useState(null);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [extraUsd, setExtraUsd] = useState(50);
  const [extraGroup, setExtraGroup] = useState('');
  const [extraMode, setExtraMode] = useState('percent');
  const [extraPercent, setExtraPercent] = useState(50);
  const [extraTotalTon, setExtraTotalTon] = useState('');
  const pollRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const api = getAuthenticatedAxios();
        const r = await api.get(`${API_URL}/api/wallets/groups`);
        setGroups((r.data?.groups || []).map((g) => g.group_name || g));
      } catch (e) { /* ignore */ }
    })();
  }, []);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const body = () => ({
    sell_group: form.sell_group,
    total_tokens: parseFloat(form.total_tokens) || 0,
    wallet_count: parseInt(form.wallet_count) || 0,
    duration_minutes: parseFloat(form.duration_minutes) || 10,
    extra_wallet: form.extra_wallet.trim() || null,
    buyback_mode: form.buyback_mode,
    buyback_slippage: parseFloat(form.buyback_slippage) || 15,
    gentle_chunk_usd: parseFloat(form.gentle_chunk_usd) || 5,
    gentle_slippage: parseFloat(form.gentle_slippage) || 1,
    buyback_pause_seconds: parseFloat(form.buyback_pause_seconds) || 45,
    chunk_min: parseFloat(form.chunk_min) || 0,
    chunk_max: parseFloat(form.chunk_max) || 0,
  });

  const doPlan = async () => {
    setPlanning(true); setErr(null); setPlan(null);
    try {
      const api = getAuthenticatedAxios();
      const r = await api.post(`${BASE}/plan`, body());
      if (r.data?.success) setPlan(r.data); else setErr(r.data?.error || 'plan failed');
    } catch (e) { setErr(e?.message || 'plan error'); }
    setPlanning(false);
  };

  const poll = useCallback(async (id) => {
    try {
      const api = getAuthenticatedAxios();
      const r = await api.get(`${BASE}/status/${id}`);
      if (r.data?.success) {
        setStatus(r.data);
        if (['completed', 'cancelled', 'failed'].includes(r.data.status)) {
          clearInterval(pollRef.current); pollRef.current = null;
        }
      }
    } catch (e) { /* ignore */ }
  }, []);

  const doStart = async () => {
    if (!window.confirm(`Запустить СЛИВ ${form.total_tokens} токенов с группы ${form.sell_group}? Это реальные сделки.`)) return;
    setBusy(true); setErr(null);
    try {
      const api = getAuthenticatedAxios();
      const r = await api.post(`${BASE}/start`, body());
      if (r.data?.success) {
        setTaskId(r.data.task_id);
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = setInterval(() => poll(r.data.task_id), 5000);
        poll(r.data.task_id);
      } else setErr(r.data?.error || 'start failed');
    } catch (e) { setErr(e?.message || 'start error'); }
    setBusy(false);
  };

  const doEmergency = async () => {
    if (!taskId) return;
    if (!window.confirm(`АВАРИЙНО откупить всё назад + $${extraUsd} сверху? Прервёт слив.`)) return;
    setBusy(true);
    try {
      const api = getAuthenticatedAxios();
      await api.post(`${BASE}/emergency/${taskId}`, {
        extra_usd: parseFloat(extraUsd) || 0, slippage: 20,
        extra_group: extraGroup || null, extra_mode: extraMode,
        extra_percent: parseFloat(extraPercent) || 0, extra_total_ton: parseFloat(extraTotalTon) || 0,
      });
      poll(taskId);
    } catch (e) { setErr(e?.message || 'emergency error'); }
    setBusy(false);
  };

  const doCancel = async () => {
    if (!taskId) return;
    try { await getAuthenticatedAxios().post(`${BASE}/cancel/${taskId}`); poll(taskId); } catch (e) { /* */ }
  };

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const running = status && ['running'].includes(status.status);
  const phaseLabel = { sell: '🔻 Слив', buyback_wait: '⏳ Пауза перед откупом', buyback: '💰 Откуп', done: '✅ Готово', emergency_done: '🚨 Аварийный откуп' };

  const input = 'border border-gray-300 rounded px-2 py-1 text-sm w-full';
  const lbl = 'text-xs text-gray-500 mb-0.5 block';

  return (
    <Card>
      <CardHeader>
        <CardTitle>📉➡️📈 Слив → Откуп (smart trading)</CardTitle>
        <CardDescription>
          Слить N токенов с кошельков группы (мелкие первыми, в ноль) за заданное время, затем синхронный откуп на весь выручённый TON. Доп.кошелёк только доливает в слив.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* ── форма ── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
          <div>
            <label className={lbl}>Группа слива</label>
            <select className={input} value={form.sell_group} onChange={(e) => upd('sell_group', e.target.value)}>
              <option value="">— выбрать —</option>
              {groups.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
          <div><label className={lbl}>Кол-во токенов</label><input className={input} type="number" value={form.total_tokens} onChange={(e) => upd('total_tokens', e.target.value)} placeholder="напр. 2000000" /></div>
          <div><label className={lbl}>Кошельков (0=все, размазать)</label><input className={input} type="number" value={form.wallet_count} onChange={(e) => upd('wallet_count', e.target.value)} placeholder="0 = все" /></div>
          <div><label className={lbl}>Длительность слива, мин</label><input className={input} type="number" value={form.duration_minutes} onChange={(e) => upd('duration_minutes', e.target.value)} /></div>
          <div><label className={lbl}>Порция от, токенов</label><input className={input} type="number" value={form.chunk_min} onChange={(e) => upd('chunk_min', e.target.value)} placeholder="0 = весь мешок" /></div>
          <div><label className={lbl}>Порция до, токенов</label><input className={input} type="number" value={form.chunk_max} onChange={(e) => upd('chunk_max', e.target.value)} placeholder="0 = весь мешок" /></div>
          <div className="col-span-2"><label className={lbl}>Доп. кошелёк (только слив)</label><input className={input} value={form.extra_wallet} onChange={(e) => upd('extra_wallet', e.target.value)} placeholder="UQ… (опционально)" /></div>
          <div>
            <label className={lbl}>Режим откупа</label>
            <select className={input} value={form.buyback_mode} onChange={(e) => upd('buyback_mode', e.target.value)}>
              <option value="aggressive">Агрессивный (высокий слиппедж)</option>
              <option value="gentle">Мягкий (мелкие, низкий слиппедж)</option>
            </select>
          </div>
          {form.buyback_mode === 'aggressive' ? (
            <div><label className={lbl}>Слиппедж откупа, %</label><input className={input} type="number" value={form.buyback_slippage} onChange={(e) => upd('buyback_slippage', e.target.value)} /></div>
          ) : (
            <>
              <div><label className={lbl}>Чанк, $</label><input className={input} type="number" value={form.gentle_chunk_usd} onChange={(e) => upd('gentle_chunk_usd', e.target.value)} /></div>
              <div><label className={lbl}>Слиппедж чанка, %</label><input className={input} type="number" value={form.gentle_slippage} onChange={(e) => upd('gentle_slippage', e.target.value)} /></div>
            </>
          )}
        </div>

        <div className="flex flex-wrap gap-2 mb-3">
          <button onClick={doPlan} disabled={planning || !form.sell_group || !form.total_tokens} className="px-3 py-1.5 rounded bg-gray-100 hover:bg-gray-200 text-sm disabled:opacity-50">{planning ? '...' : 'Превью плана'}</button>
          <button onClick={doStart} disabled={busy || !plan || running} className="px-3 py-1.5 rounded bg-red-600 hover:bg-red-700 text-white text-sm disabled:opacity-50">Старт слива</button>
          {running && <button onClick={doCancel} className="px-3 py-1.5 rounded bg-gray-200 hover:bg-gray-300 text-sm">Отменить слив</button>}
        </div>
        {err && <div className="text-red-600 text-sm mb-2">{err}</div>}

        {/* ── превью плана ── */}
        {plan && !status && (
          <div className="border border-gray-200 rounded p-3 mb-3 text-sm">
            <div className="font-semibold mb-1">План (dry-run)</div>
            <div className="flex flex-wrap gap-3 text-xs">
              <span>цена: <b>{fmtPrice(plan.token_price_usd)}</b></span>
              <span>запрошено: <b>{fmtTok(plan.requested_tokens)}</b></span>
              <span>план: <b>{fmtTok(plan.planned_tokens)}</b>{plan.shortfall_tokens > 0 && <span className="text-amber-600"> (не хватит {fmtTok(plan.shortfall_tokens)})</span>}</span>
              <span>кошельков: <b>{plan.sell_wallets}</b></span>
              {plan.est_chunks > plan.sell_wallets && <span>порций: <b>{plan.est_chunks}</b></span>}
              <span>выручка ≈ <b>{plan.est_proceeds_ton} TON</b> (${plan.est_proceeds_usd})</span>
              {plan.wallets_no_gas > 0 && <span className="text-amber-600">без газа: {plan.wallets_no_gas}</span>}
            </div>
            <div className="mt-2 max-h-32 overflow-auto text-xs font-mono">
              {plan.legs.map((l, i) => <div key={i} className="text-gray-600">{l.address.slice(0, 12)}… слить {fmtTok(l.sell_tokens)} из {fmtTok(l.balance)} {l.zeroed ? '⟶0' : 'частично'}</div>)}
              {plan.extra_wallet && <div className="text-orange-600">доп: {plan.extra_wallet.address.slice(0, 12)}… слить {fmtTok(plan.extra_wallet.balance)} (без откупа)</div>}
            </div>
          </div>
        )}

        {/* ── реал-тайм статус ── */}
        {status && (
          <div className="border border-blue-200 bg-blue-50/40 rounded p-3 text-sm">
            <div className="flex items-center justify-between mb-2">
              <div className="font-semibold">{phaseLabel[status.phase] || status.phase} · <span className="uppercase text-xs">{status.status}</span></div>
              <div className="flex flex-wrap items-center gap-1">
                <span className="text-[10px] text-gray-500">+$</span>
                <input type="number" value={extraUsd} onChange={(e) => setExtraUsd(e.target.value)} className="border border-gray-300 rounded px-1 py-0.5 w-14 text-xs" title="доп. $ размазать по продавшим" />
                <select value={extraGroup} onChange={(e) => setExtraGroup(e.target.value)} className="border border-gray-300 rounded px-1 py-0.5 text-xs" title="доп. группа для откупа">
                  <option value="">+ группа</option>
                  {groups.map((g) => <option key={g} value={g}>{g}</option>)}
                </select>
                {extraGroup && (
                  <>
                    <select value={extraMode} onChange={(e) => setExtraMode(e.target.value)} className="border border-gray-300 rounded px-1 py-0.5 text-xs">
                      <option value="percent">% депозита</option>
                      <option value="total_ton">TON всего</option>
                    </select>
                    {extraMode === 'percent'
                      ? <input type="number" value={extraPercent} onChange={(e) => setExtraPercent(e.target.value)} placeholder="%" className="border border-gray-300 rounded px-1 py-0.5 w-12 text-xs" />
                      : <input type="number" value={extraTotalTon} onChange={(e) => setExtraTotalTon(e.target.value)} placeholder="TON" className="border border-gray-300 rounded px-1 py-0.5 w-14 text-xs" />}
                  </>
                )}
                <button onClick={doEmergency} disabled={busy} className="px-2 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-xs disabled:opacity-50">🚨 Откупить всё</button>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs mb-2">
              <span className="px-2 py-1 rounded bg-white">цена: <b>{fmtPrice(status.current_price_usd)}</b></span>
              <span className="px-2 py-1 rounded bg-white">слито: <b>{fmtTok(status.sold_tokens_total)}</b> ({status.sell_legs_done}/{status.sell_legs_planned})</span>
              <span className="px-2 py-1 rounded bg-white">выручка: <b>{status.proceeds_ton_total} TON</b></span>
              <span className="px-2 py-1 rounded bg-white">откуплено: <b>{status.spent_ton_total} TON</b> ({status.buyback_done})</span>
              <span className="px-2 py-1 rounded bg-red-50 text-red-700 col-span-2 md:col-span-4">🎯 ВРАГИ скинули в окне: <b>{fmtTok(status.enemy?.enemy_sold_tokens)}</b> токенов (${status.enemy?.enemy_sold_usd}, {status.enemy?.enemy_sellers} кош.)</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <div className="bg-white rounded p-2 max-h-40 overflow-auto text-[11px] font-mono">
                <div className="text-gray-400 mb-1">Кошельки</div>
                {(status.sold || []).map((r, i) => <div key={i} className={r.status?.startsWith('success') ? 'text-gray-700' : 'text-red-500'}>{r.extra ? '(доп)' : ''}{r.address.slice(0, 10)}… −{fmtTok(r.tokens_sold)} +{r.ton_received}T [{r.status}]</div>)}
                {(status.bought || []).map((b, i) => <div key={`b${i}`} className="text-emerald-600">↩ {b.address.slice(0, 10)}… откуп {b.spent_ton || b.spent_usd || ''} [{b.status}]</div>)}
              </div>
              <div className="bg-white rounded p-2 max-h-40 overflow-auto text-[11px]">
                <div className="text-gray-400 mb-1">Лог</div>
                {(status.logs || []).slice().reverse().map((l, i) => <div key={i} className={l.level === 'error' ? 'text-red-500' : l.level === 'warning' ? 'text-amber-600' : 'text-gray-600'}>{l.msg}</div>)}
              </div>
            </div>
          </div>
        )}
        <div className="mt-2 text-[11px] text-gray-400">
          ⚠️ Это wash-цикл: round-trip = слиппедж×2 + комиссии. «Профит» только если враги сливают в дамп ниже твоего откупа (следи за строкой «ВРАГИ скинули»). Counter-trading на паузе во время цикла.
        </div>
      </CardContent>
    </Card>
  );
};

export default DumpBuyback;
